import React, { useMemo, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Home,
  FileText,
  Building2,
  Menu,
  X,
  LogOut,
  Workflow,
  Users,
  BarChart3,
  Coins,
  ScrollText,
  // CreditCard
} from 'lucide-react';
import { decryptData } from '../utils/encryption';

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  // const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const userPermissions = localStorage.getItem('permissions') || '';

  const permissionRole = useMemo(() => {
    if (!userPermissions) {
      return '';
    }
    try {
      return decryptData(userPermissions);
    } catch (error) {
      console.error('Failed to decrypt permissions:', error);
      return '';
    }
  }, [userPermissions]);

  const isAdmin = permissionRole === 'admin' || permissionRole === 'super_admin';

  const handleLogoutConfirm = () => {
    logout();
    setShowLogoutModal(false);
    navigate("/login");
  };

  // Fetch user permissions on component mount
  // useEffect(() => {
  //   const fetchPermissions = async () => {
  //     try {
  //       const response = await axios.get('/api/users/permissions', {
  //         headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  //       });
  //       setUserPermissions(response.data.data.permissions || []);
  //     } catch (error) {
  //       console.error('Failed to fetch user permissions:', error);
  //     }
  //   };

  //   if (user) {
  //     fetchPermissions();
  //   }
  // }, [user]);

  // Check if user has permission
  const hasPermission = (_permission: string): boolean => {
    // Temporarily allow all permissions for presentation
    return true;
    // return userPermissions.includes(permission);
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Invoices', href: '/invoices', icon: FileText },
    { name: 'Financial Approvals', href: '/financial-power-proposal', icon: ScrollText },
    { name: 'Vendors', href: '/vendors', icon: Building2 },
    // { name: 'Estimates', href: '/estimates', icon: Calculator },
    // { name: 'Bills', href: '/bills', icon: Receipt },
    // { name: 'Time Entries', href: '/time-entries', icon: Clock },
    // { name: 'Workflow', href: '/workflows', icon: Workflow },
    { name: 'Department', href: '/departments', icon: Workflow },
    { name: 'Currencies', href: '/currencies', icon: Coins, adminOnly: true },
    // { name: 'Compliance', href: '/compliance-reports', icon: Shield },
    { name: 'User Management', href: '/user-management', icon: Users, permission: 'read_users' },
    { name: 'Reports', href: '/reports', icon: BarChart3, superAdminOnly: true },
    // { name: 'Financial Power Invoice', href: '/financial-power-invoice', icon: CreditCard },
    // { name: 'Settings', href: '/settings', icon: Settings },
  ];

  const filteredNavigation = navigation.filter((item) => {
    // Super admin sees everything
    if (isAdmin) {
      return true;
    }

    // Hide super admin only items
    if ((item as any).superAdminOnly) {
      return false;
    }

    // Hide admin only items for non-admin users
    if ((item as any).adminOnly) {
      return false;
    }

    // Non-admin roles
    if (permissionRole === "invoice_initiator") {
      // Hide only User Management and Department
      return item.name !== "User Management" && item.name !== "Department" && item.name !== "Currencies" && item.name !== "Vendors" && item.name !== "Dashboard";
    } 
    else if (permissionRole === "finance_team") {
      // Hide User Management, Department, Vendors, Currencies, Financial Power Invoice
      return item.name !== "User Management" && item.name !== "Department" && item.name !== "Vendors" && item.name !== "Currencies" && item.name !== "Financial Power Proposal" && item.name !== "Dashboard" && item.name !== "Financial Approvals";
    }
    else if (permissionRole === "payment_approver") {
      // Hide User Management, Department, Vendors, Currencies
      return item.name !== "User Management" && item.name !== "Department" && item.name !== "Vendors" && item.name !== "Currencies" && item.name !== "Financial Power Proposal" && item.name !== "Dashboard" && item.name !== "Financial Approvals";
    }
    else {
      // Hide User Management, Department, Vendors, Currencies for approvers
      return item.name !== "User Management" && item.name !== "Department" && item.name !== "Vendors" && item.name !== "Currencies" && item.name !== "Dashboard";
    }
  });



  return (
    <div className="h-screen flex overflow-hidden bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 flex z-50 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-primary-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-lg">V</span>
                  </div>
                </div>
                <div className="ml-3">
                  <h1 className="text-xl font-bold text-gray-900">VMS</h1>
                  <p className="text-sm text-gray-500">Vendor Management</p>
                </div>
              </div>
            </div>
            <nav className="mt-8 px-2 space-y-1">
              {filteredNavigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;

                // Check if user has permission to see this menu item
                if (item.permission && !hasPermission(item.permission)) {
                  return null;
                }

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${isActive
                      ? 'sidebar-item-active'
                      : 'sidebar-item-inactive'
                      } sidebar-item`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className="mr-3 h-5 w-5" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 sidebar">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 bg-primary-600 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-lg">V</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <h1 className="text-xl font-bold text-gray-900">VMS</h1>
                    <p className="text-sm text-gray-500">Vendor Management</p>
                  </div>
                </div>
              </div>
              <nav className="mt-8 flex-1 px-2 space-y-1">
                {filteredNavigation.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href;

                  // Check if user has permission to see this menu item
                  if (item.permission && !hasPermission(item.permission)) {
                    return null;
                  }

                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`${isActive
                        ? 'sidebar-item-active'
                        : 'sidebar-item-inactive'
                        } sidebar-item`}
                    >
                      <Icon className="mr-3 h-5 w-5" />
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top bar */}
        <div className="relative z-10 flex-shrink-0 flex h-16 header">
          <button
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1 px-4 flex justify-between items-center">
            <div className="flex-1 flex max-w-lg">
              <div className="w-full flex md:ml-0">
                {/* <div className="relative w-full text-gray-400 focus-within:text-gray-600">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5" />
                  </div>
                  <input
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
                    placeholder="Search vendor invoices, vendors..."
                    type="search"
                  />
                </div> */}
              </div>
            </div>
            <div className="ml-4 flex items-center space-x-3">
              {/* Notifications */}
              {/* <button className="p-2 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors">
                <Bell className="h-5 w-5" />
              </button> */}

              {/* Quick Actions */}
              {/* <button className="btn-primary flex items-center" onClick={handleUploadInvoice}>
                <Upload className="h-4 w-4 mr-2" />
                Upload Invoice
              </button> */}

              {/* User menu */}
              <div className="relative">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 bg-primary-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-medium text-sm">
                        {user?.first_name?.[0]}{user?.last_name?.[0]}
                      </span>
                    </div>
                    <div className="hidden md:block">
                      <div className="text-sm font-medium text-gray-900">
                        {user?.first_name} {user?.last_name}
                      </div>
                      <div className="text-xs text-gray-500">{user?.email}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowLogoutModal(true)}
                    className="p-2 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                    title="Logout"
                  >
                    <LogOut className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>

      {/* Logout Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-md shadow-lg w-96 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Logout</h3>
              <button
                onClick={() => setShowLogoutModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <p className="text-sm text-gray-900 mb-6">
              Are you sure you want to logout from your account?
            </p>
            <div className="flex justify-center space-x-3">
              <button
                type="button"
                onClick={() => setShowLogoutModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLogoutConfirm}
                className="btn-primary bg-primary-600 hover:bg-primary-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Layout; 